/**
 * TaskScheduler para gestionar notificaciones programadas en el frontend
 * Maneja schedules/crons programados y los modifica según eventos WebSocket
 */

import type { EventData, ScheduledTask, PersistedTask, TaskSchedulerConfig, EventSchedule } from '@/api/definitions';
import { describeCronExpression, validateCronExpression } from './cron-generator';

/**
 * Singleton TaskScheduler para gestionar schedules de notificaciones
 */
class TaskScheduler {
  private static instance: TaskScheduler | null = null;
  private readonly scheduledTasks: Map<string, ScheduledTask> = new Map();
  private config: TaskSchedulerConfig | null = null;
  private isInitialized: boolean = false;
  private readonly STORAGE_KEY = 'taskScheduler_tasks';
  private readonly STORAGE_VERSION_KEY = 'taskScheduler_version';
  private readonly CURRENT_STORAGE_VERSION = 1;

  private constructor() {}

  public static getInstance(): TaskScheduler {
    if (!TaskScheduler.instance) {
      TaskScheduler.instance = new TaskScheduler();
    }
    return TaskScheduler.instance;
  }

  public initialize(config: TaskSchedulerConfig): void {
    if (this.isInitialized) {
      this.handleError(new Error('TaskScheduler already initialized'), 'initialize');
      return;
    }
    this.config = config;
    this.isInitialized = true;
    this.loadPersistedTasks();
    this.loadScheduledEventsFromBackend();
  }

  public scheduleEvent(event: EventData, timestamp?: number): void {
    if (!this.isInitialized || !this.config) {
      this.handleError(new Error('TaskScheduler not initialized'), 'scheduleEvent');
      return;
    }

    try {
      const eventTimestamp = timestamp || Date.now();
      const existingTask = this.scheduledTasks.get(event.id);
      
      // Protección contra mensajes viejos
      if (existingTask && existingTask.lastUpdateTimestamp > eventTimestamp) {
        return;
      }

      const cronExpression = this.extractCronExpression(event);
      if (!cronExpression) {
        return;
      }

      if (!validateCronExpression(cronExpression)) {
        this.handleError(new Error(`Invalid cron expression: ${cronExpression}`), 'scheduleEvent');
        return;
      }

      this.cancelTask(event.id);

      const scheduledTask: ScheduledTask = {
        id: event.id,
        eventData: event,
        cronExpression,
        startDate: this.extractStartDate(event),
        endDate: this.extractEndDate(event),
        timezone: this.extractTimezone(event),
        isActive: true,
        executionCount: existingTask?.executionCount || 0,
        description: describeCronExpression(cronExpression),
        nextExecution: this.calculateNextExecution(cronExpression, this.extractStartDate(event)),
        lastUpdateTimestamp: eventTimestamp,
        version: (existingTask?.version || 0) + 1
      };

      this.setupTaskExecution(scheduledTask);
      this.scheduledTasks.set(event.id, scheduledTask);
      this.persistTasks();

      this.notifyScheduleUpdate();
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'scheduleEvent');
    }
  }

  public updateEvent(event: EventData, timestamp?: number): void {
    this.scheduleEvent(event, timestamp);
  }

  public deleteEvent(eventId: string): void {
    this.cancelTask(eventId);
  }

  public cancelTask(eventId: string): void {
    try {
      const task = this.scheduledTasks.get(eventId);
      if (!task) return;

      if (task.timeoutId) {
        clearTimeout(task.timeoutId);
      }

      this.scheduledTasks.delete(eventId);
      this.persistTasks();
      this.notifyScheduleUpdate();
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'cancelTask');
    }
  }

  public getScheduledTasks(): ScheduledTask[] {
    return Array.from(this.scheduledTasks.values());
  }

  public getTask(eventId: string): ScheduledTask | undefined {
    return this.scheduledTasks.get(eventId);
  }

  public clearAll(): void {
    try {
      const taskCount = this.scheduledTasks.size;
      for (const [eventId] of this.scheduledTasks) {
        this.cancelTask(eventId);
      }
      //console.log(`🧹 Cleared ${taskCount} scheduled tasks`);
      this.clearPersistedTasks();
      this.notifyScheduleUpdate();
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'clearAll');
    }
  }

  public reset(): void {
    this.clearAll();
    this.config = null;
    this.isInitialized = false;
  }

  private extractCronExpression(event: EventData): string | null {
    try {
      const schedule = event.schedule as EventSchedule | undefined;
      if (schedule?.cronExpression) return schedule.cronExpression;
      
      const eventWithCron = event as EventData & { cronExpression?: string };
      if (eventWithCron.cronExpression) return eventWithCron.cronExpression;
      
      // Si solo hay startDate, generar cron para una ejecución única
      if (schedule?.startDate) {
        const startDate = new Date(schedule.startDate);
        if (isNaN(startDate.getTime())) {
          console.warn(`[TaskScheduler] Invalid startDate: ${schedule.startDate}`);
          return null;
        }
        
        // Generar expresión cron para esa fecha/hora específica
        const minute = startDate.getMinutes();
        const hour = startDate.getHours();
        const day = startDate.getDate();
        const month = startDate.getMonth() + 1; // getMonth() es 0-indexed
        
        const cronExpression = `${minute} ${hour} ${day} ${month} *`;
        return cronExpression;
      }
      
      return null;
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'extractCronExpression');
      return null;
    }
  }

  private extractStartDate(event: EventData): string | undefined {
    try {
      const schedule = event.schedule as EventSchedule | undefined;
      if (schedule?.startDate) return schedule.startDate;
      
      const eventWithDate = event as EventData & { startDate?: string };
      return eventWithDate.startDate;
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'extractStartDate');
      return undefined;
    }
  }

  private extractEndDate(event: EventData): string | undefined {
    try {
      const schedule = event.schedule as EventSchedule | undefined;
      if (schedule?.endDate) return schedule.endDate;
      
      const eventWithDate = event as EventData & { endDate?: string };
      return eventWithDate.endDate;
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'extractEndDate');
      return undefined;
    }
  }

  private extractTimezone(event: EventData): string | undefined {
    try {
      const schedule = event.schedule as EventSchedule | undefined;
      if (schedule?.timezone) return schedule.timezone;
      
      const eventWithTimezone = event as EventData & { timezone?: string };
      return eventWithTimezone.timezone || 'Europe/Madrid';
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'extractTimezone');
      return 'Europe/Madrid';
    }
  }

  private calculateNextExecution(cronExpression: string, startDate?: string, fromTime?: Date): Date | undefined {
    try {
      const now = fromTime || new Date();
      
      if (startDate) {
        const start = new Date(startDate);
        if (isNaN(start.getTime())) {
          throw new Error(`Invalid startDate: ${startDate}`);
        }
        if (start > now) return start;
      }

      return this.parseBasicCron(cronExpression, startDate, fromTime);
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'calculateNextExecution');
      return undefined;
    }
  }

  private parseBasicCron(cronExpression: string, startDate?: string, fromTime?: Date): Date | undefined {
    try {
      if (cronExpression.startsWith('CUSTOM:EVERY_')) {
        return this.parseCustomCron(cronExpression, startDate, fromTime);
      }

      const parts = cronExpression.trim().split(/\s+/);
      if (parts.length !== 5) return undefined;

      const [minute, hour] = parts;
      const now = fromTime || new Date();
      const next = new Date(now);

      if (hour.startsWith('*/')) {
        const interval = parseInt(hour.replace('*/', ''), 10);
        const targetMinute = parseInt(minute, 10);
        
        if (isNaN(interval) || isNaN(targetMinute) || interval <= 0 || targetMinute < 0 || targetMinute > 59) {
          throw new Error(`Invalid cron values: hour interval=${interval}, minute=${targetMinute}`);
        }
        
        next.setMinutes(targetMinute, 0, 0);
        
        if (next <= now) {
          const hoursToAdd = interval - (now.getHours() % interval);
          next.setHours(now.getHours() + hoursToAdd);
        }
        
        return next;
      }

      if (minute !== '*' && hour !== '*') {
        const targetHour = parseInt(hour, 10);
        const targetMinute = parseInt(minute, 10);
        
        if (isNaN(targetHour) || isNaN(targetMinute) || targetHour < 0 || targetHour > 23 || targetMinute < 0 || targetMinute > 59) {
          throw new Error(`Invalid cron values: hour=${targetHour}, minute=${targetMinute}`);
        }
        
        next.setHours(targetHour, targetMinute, 0, 0);
        
        if (next <= now) {
          next.setDate(next.getDate() + 1);
        }
        
        return next;
      }

      return undefined;
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'parseBasicCron');
      return undefined;
    }
  }

  private parseCustomCron(cronExpression: string, startDate?: string, fromTime?: Date): Date | undefined {
    try {
      const parts = cronExpression.split(':');
      if (parts.length < 4) return undefined;

      const dayInterval = parseInt(parts[1].replace('EVERY_', '').replace('_DAYS', ''), 10);
      const minute = parseInt(parts[2], 10);
      const hour = parseInt(parts[3], 10);

      if (isNaN(dayInterval) || isNaN(minute) || isNaN(hour) || 
          dayInterval <= 0 || minute < 0 || minute > 59 || hour < 0 || hour > 23) {
        throw new Error(`Invalid custom cron values: dayInterval=${dayInterval}, minute=${minute}, hour=${hour}`);
      }

      const start = startDate ? new Date(startDate) : (fromTime || new Date());
      if (startDate && isNaN(start.getTime())) {
        throw new Error(`Invalid startDate: ${startDate}`);
      }
      
      const next = new Date(start);
      next.setHours(hour, minute, 0, 0);

      const now = fromTime || new Date();
      while (next <= now) {
        next.setDate(next.getDate() + dayInterval);
      }

      return next;
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'parseCustomCron');
      return undefined;
    }
  }

  private setupTaskExecution(task: ScheduledTask): void {
    if (!task.nextExecution) {
      return;
    }

    const now = new Date();
    const delay = task.nextExecution.getTime() - now.getTime();

    if (delay <= 0) {
      this.executeTask(task);
    } else {
      task.timeoutId = window.setTimeout(() => {
        this.executeTask(task);
      }, delay);
    }
  }

  private executeTask(task: ScheduledTask): void {
    if (!this.config || !task.isActive) {
      return;
    }

    try {
      if (task.endDate) {
        const endDate = new Date(task.endDate);
        if (isNaN(endDate.getTime())) {
          throw new Error(`Invalid endDate for task ${task.id}: ${task.endDate}`);
        }
        if (new Date() > endDate) {
          this.cancelTask(task.id);
          return;
        }
      }

      task.executionCount++;
      
      try {
        this.config.onNotificationExecute(task.eventData);
      } catch (error) {
        console.error(`Error executing task ${task.id}:`, error);
        this.config.onError(error instanceof Error ? error : new Error(String(error)), 'executeTask');
      }

      task.lastUpdateTimestamp = Date.now();
      task.version++;
      
      // Si es una notificación de una sola vez (solo startDate), cancelarla después de ejecutar
      const schedule = task.eventData.schedule as any;
      const isOneTimeNotification = schedule?.startDate && !schedule?.cronExpression;
      
      if (isOneTimeNotification) {
        this.cancelTask(task.id);
      } else {
        // Para notificaciones recurrentes, calcular siguiente ejecución
        const afterExecution = new Date(Date.now() + 60 * 1000);
        task.nextExecution = this.calculateNextExecution(task.cronExpression, task.startDate, afterExecution);
        if (task.nextExecution) {
          this.setupTaskExecution(task);
          this.persistTasks();
        } else {
          this.cancelTask(task.id);
        }
      }

      this.notifyScheduleUpdate();
    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)), 'executeTask');
    }
  }

  private notifyScheduleUpdate(): void {
    if (this.config) {
      this.config.onScheduleUpdate(this.getScheduledTasks());
    }
  }

  private handleError(error: Error, context?: string): void {
    const errorMessage = context ? `[${context}] ${error.message}` : error.message;
    console.error('TaskScheduler Error:', errorMessage);
    
    if (this.config?.onError) {
      this.config.onError(error, context);
    }
  }

  private persistTasks(): void {
    try {
      const tasksToSave: PersistedTask[] = [];
      
      for (const task of this.scheduledTasks.values()) {
        tasksToSave.push({
          id: task.id,
          eventData: task.eventData,
          cronExpression: task.cronExpression,
          startDate: task.startDate,
          endDate: task.endDate,
          timezone: task.timezone,
          isActive: task.isActive,
          nextExecution: task.nextExecution?.toISOString(),
          executionCount: task.executionCount,
          description: task.description,
          lastUpdateTimestamp: task.lastUpdateTimestamp,
          version: task.version
        });
      }
      
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(tasksToSave));
      localStorage.setItem(this.STORAGE_VERSION_KEY, String(this.CURRENT_STORAGE_VERSION));
    } catch (error) {
      console.warn('Failed to persist tasks to localStorage:', error);
    }
  }
  
  private loadPersistedTasks(): void {
    try {
      const storedVersion = localStorage.getItem(this.STORAGE_VERSION_KEY);
      if (Number(storedVersion) !== this.CURRENT_STORAGE_VERSION) {
        this.clearPersistedTasks();
        return;
      }
      
      const storedTasks = localStorage.getItem(this.STORAGE_KEY);
      if (!storedTasks) return;
      
      const persistedTasks: PersistedTask[] = JSON.parse(storedTasks);
      let restoredCount = 0;
      
      for (const persistedTask of persistedTasks) {
        try {
          if (!this.isTaskValid(persistedTask)) {
            continue;
          }
          
          const scheduledTask: ScheduledTask = {
            ...persistedTask,
            nextExecution: persistedTask.nextExecution ? new Date(persistedTask.nextExecution) : undefined,
            timeoutId: undefined
          };
          
          if (this.shouldRestoreTask(scheduledTask)) {
            this.scheduledTasks.set(scheduledTask.id, scheduledTask);
            this.setupTaskExecution(scheduledTask);
            restoredCount++;
          }
        } catch (taskError) {
          console.warn(`Failed to restore task ${persistedTask.id}:`, taskError);
        }
      }
      
      if (restoredCount > 0) {
        this.notifyScheduleUpdate();
      }
    } catch (error) {
      console.warn('Failed to load persisted tasks:', error);
      this.clearPersistedTasks();
    }
  }
  
  private isTaskValid(task: PersistedTask): boolean {
    return !!(task.id && task.cronExpression && task.eventData && 
             validateCronExpression(task.cronExpression));
  }
  
  private shouldRestoreTask(task: ScheduledTask): boolean {
    if (task.endDate) {
      const endDate = new Date(task.endDate);
      if (new Date() > endDate) return false;
    }
    
    if (task.nextExecution) {
      const timeDiff = Date.now() - task.nextExecution.getTime();
      const maxStaleTime = 24 * 60 * 60 * 1000; // 24 horas
      if (timeDiff > maxStaleTime) return false;
    }
    
    return true;
  }
  
  private clearPersistedTasks(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
      localStorage.removeItem(this.STORAGE_VERSION_KEY);
    } catch (error) {
      console.warn('Failed to clear persisted tasks:', error);
    }
  }

  public getStatus(): boolean {
    return this.isInitialized;
  }

  /**
   * Carga eventos programados desde el backend al inicializar
   */
  private async loadScheduledEventsFromBackend(): Promise<void> {
    try {
      const { ApiEvents } = await import('@/api/api-events');
      const { Request } = await import('@asanrom/request-browser');
      
      Request.Pending(
        "load-scheduled-events",
        ApiEvents.GetScheduledEvents()
      )
      .onSuccess((data) => {
        const events = data.events || data;
        
        if (Array.isArray(events)) {
          for (const event of events) {
            // Solo cargar si no existe ya en localStorage (localStorage tiene prioridad)
            if (!this.scheduledTasks.has(event.id)) {
              this.scheduleEvent(event, Date.now());
            }
          }
        }
      })
      .onRequestError((err, handleErr) => {
        console.warn('Failed to load scheduled events from backend:', err);
        handleErr(err, {
          unauthorized: () => {
          },
          temporalError: () => {
          },
          badRequest: () => {
          },
        });
      })
      .onUnexpectedError((error) => {
        console.warn('Unexpected error loading scheduled events:', error);
      });
    } catch (error) {
      console.warn('Failed to load scheduled events from backend:', error);
    }
  }
}

// Exportar singleton
export const taskScheduler = TaskScheduler.getInstance();

// Función helper para obtener el scheduler
export function getTaskScheduler(): TaskScheduler {
  return taskScheduler;
}